import React from 'react';
import { Phone, MessageCircle, CheckCircle2, Percent, Gift, Users, CreditCard, Heart, Sparkles } from 'lucide-react';
import { COMPANY } from '../../mock';
import { Button } from '../ui/button';

const OFFER_ICONS = { Percent, Heart, Users, Gift, CreditCard };

const SEASON_LABELS = {
  'shimla': 'SHIMLA SEASON 2026',
  'manali': 'SNOWFALL SEASON 2026',
  'himachal-pradesh': 'HIMACHAL SEASON 2026',
  'kashmir': 'KASHMIR SEASON 2026',
  'jammu-kashmir': 'KASHMIR SEASON 2026',
  'leh-ladakh': 'LEH & LADAKH SEASON 2026',
  'spiti-valley': 'SPITI KINNAUR SEASON 2026',
  'kinnaur': 'SPITI KINNAUR SEASON 2026',
  'spiti-kinnaur': 'SPITI KINNAUR SEASON 2026',
  'rajasthan': 'RAJASTHAN SEASON 2026',
  'uttarakhand': 'UTTARAKHAND SEASON 2026',
  'chardham-uttarakhand': 'CHARDHAM YATRA SEASON 2026',
};

const HERO_IMAGES = {
  'shimla': '/images/hero/shimla.jpg',
  'manali': '/images/hero/manali.jpg',
  'himachal-pradesh': '/images/hero/himachal.jpg',
  'kashmir': '/images/hero/kashmir.jpg',
  'jammu-kashmir': '/images/hero/kashmir.jpg',
  'leh-ladakh': '/images/hero/leh-ladakh.jpg',
  'spiti-valley': '/images/hero/spiti-kinnaur.jpg',
  'kinnaur': '/images/hero/spiti-kinnaur.jpg',
  'spiti-kinnaur': '/images/hero/spiti-kinnaur.jpg',
  'rajasthan': '/images/hero/rajasthan.jpg',
  'uttarakhand': '/images/hero/uttarakhand.jpg',
  'chardham-uttarakhand': '/images/hero/chardham.jpg',
};

const HERO_DESCRIPTIONS = {
  'shimla': 'Explore Shimla, Kufri, Naldhera, Kullu-Manali, Solang Valley, Kasol, Jibhi, McLeod Ganj, Bir, Tirthan Valley, Sangla & Chitkul',
  'manali': 'Adventure, snowfall & luxury - all in one trip. Create lifetime memories in Himachal with India\'s most loved travel family.',
  'himachal-pradesh': 'Explore Shimla, Manali, Dharamshala, Dalhousie, Kasol, Kinnaur and Spiti with customized honeymoon, family and group tour packages at unbeatable prices.',
  'kashmir': 'Explore Srinagar, Pahalgam, Gulmarg, Sonmarg, Dal Lake houseboats & Vaishno Devi with customized honeymoon, family and group tour packages at unbeatable prices.',
  'jammu-kashmir': 'Explore Srinagar, Pahalgam, Gulmarg, Sonmarg, Dal Lake houseboats & Vaishno Devi with customized honeymoon, family and group tour packages at unbeatable prices.',
  'leh-ladakh': 'Explore Pangong Lake, Nubra Valley, Turtuk & famous Ladakh destinations with customized honeymoon, family, group & bike tour packages at unbeatable prices.',
  'spiti-valley': 'Explore Shimla, Sarahan, Sangla, Kalpa, Chitkul, Nako, Tabo, Kaza, Chandratal, Atal Tunnel, Zanskar & Spiti Valley with customized honeymoon, family & group tour packages at unbeatable prices.',
  'kinnaur': 'Explore Shimla, Sarahan, Sangla, Kalpa, Chitkul, Nako, Tabo, Kaza, Chandratal, Atal Tunnel, Zanskar & Spiti Valley with customized honeymoon, family & group tour packages at unbeatable prices.',
  'spiti-kinnaur': 'Explore Shimla, Sarahan, Sangla, Kalpa, Chitkul, Nako, Tabo, Kaza, Chandratal, Atal Tunnel, Zanskar & Spiti Valley with customized honeymoon, family & group tour packages at unbeatable prices.',
  'rajasthan': 'Explore Jaipur (the Pink City) for historic forts, Udaipur (City of Lakes) for boat rides, Jaisalmer (Sam Sand Dunes), and Jodhpur (the Blue City), Mount Abu with customized family, group, senior citizen and spiritual tour packages.',
  'uttarakhand': 'Rishikesh · Nainital & Bhimtal · Mussoorie · Haridwar · Char Dham Circuit · Auli · Valley of Flowers & Hemkund Sahib · Badrinath · Jim Corbett · Panch Kedar',
  'chardham-uttarakhand': 'Explore Haridwar – Yamunotri – Gangotri – Kedarnath – Badrinath with customized family, group, senior citizen and spiritual chardham yatra tour packages.',
};

const TRUST_BADGES = [
  { text: 'Govt. Registered', sub: 'Himachal & J&K Tourism' },
  { text: '15+ Years', sub: 'Experience' },
  { text: '10 Lakh+', sub: 'Happy Travelers' },
  { text: '4.9/5', sub: 'Customer Rating' },
];

export default function HeroSection({ slug = 'shimla', name = '', startingPrice = 0, image: customImage }) {
  const image = customImage || HERO_IMAGES[slug] || '/images/hero/shimla.jpg';
  const seasonLabel = SEASON_LABELS[slug] || 'SEASON 2026';
  const description = HERO_DESCRIPTIONS[slug] || '';
  const destName = name || slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  return (
    <section className="relative min-h-[85vh] w-full overflow-hidden bg-brand-blue">
      {/* Background image */}
      <img 
        src={image} 
        alt={destName}
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/90 via-brand-blue/70 to-brand-blue/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/60 via-transparent to-brand-blue/30" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 h-full min-h-[85vh] flex items-center">
        <div className="grid lg:grid-cols-2 gap-10 w-full py-20 items-center">
          {/* Left - Main content */}
          <div className="text-white">
            {/* Season badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-[0.2em] text-brand-orange mb-6">
              <Sparkles size={14} /> {seasonLabel} · Limited Slots
            </div>

            {/* Heading */}
            <h1 className="font-poppins font-extrabold text-[28px] sm:text-[42px] lg:text-[64px] leading-[1.05] mb-5 text-white">
              {destName}
            </h1>

            {/* Description */}
            {description && (
              <p className="text-white/80 text-[16px] lg:text-[18px] leading-relaxed max-w-xl mb-6">
                {description}
              </p>
            )}

            {/* Price */}
            {startingPrice > 0 && (
              <div className="mb-6">
                <div className="text-white/60 text-[12px] font-semibold uppercase tracking-wider mb-1">Starting From</div>
                <div className="flex items-baseline gap-2">
                  <span className="font-poppins font-bold text-[32px] sm:text-[44px] leading-none text-white">₹{startingPrice.toLocaleString('en-IN')}</span>
                  <span className="text-white/60 text-[16px]">/person</span>
                </div>
              </div>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-3 mb-8">
              <a href={`tel:${COMPANY.phone}`}>
                <Button className="bg-brand-orange hover:bg-brand-orangeDark text-white h-12 px-8 rounded-xl font-bold text-[15px] btn-glow flex items-center gap-2">
                  <Phone size={18} /> Call Now
                </Button>
              </a>
              <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'m planning a ' + destName + ' trip and would love a customized itinerary.\n\nPlease share:\n- Available tour packages\n- Pricing & hotel options\n- Best time to visit\n\nThank you!')}`} target="_blank" rel="noreferrer">
                <Button className="bg-white/10 border border-white/25 hover:bg-white/20 text-white h-12 px-8 rounded-xl font-bold text-[15px] flex items-center gap-2 backdrop-blur-sm">
                  <MessageCircle size={18} /> WhatsApp Now
                </Button>
              </a>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-6">
              {TRUST_BADGES.map((b, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
                  <div>
                    <div className="text-white font-bold text-[13px]">{b.text}</div>
                    <div className="text-white/50 text-[11px]">{b.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Offers + Quote */}
          <div className="lg:pl-10">
            {/* Offers grid */}
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-6 mb-5">
              <div className="text-brand-orange text-[11px] font-bold uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                <Gift size={14} /> Live Offers · Limited Time
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: 'Percent', label: 'Flat 25% OFF', desc: 'On all packages' },
                  { icon: 'Heart', label: 'Honeymoon Special', desc: 'Free candlelight dinner' },
                  { icon: 'Users', label: 'Group Discount', desc: 'Save ₹2,000/pp (10+)' },
                  { icon: 'Gift', label: 'Early Bird', desc: 'Extra 15% OFF' },
                ].map((offer, i) => {
                  const Icon = OFFER_ICONS[offer.icon] || Gift;
                  return (
                    <div key={i} className="bg-white/5 rounded-xl p-3 border border-white/10">
                      <Icon size={18} className="text-brand-orange mb-1" />
                      <div className="text-white font-bold text-[12px] leading-tight">{offer.label}</div>
                      <div className="text-white/50 text-[10px]">{offer.desc}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quick quote */}
            <div className="bg-brand-orange rounded-3xl p-6 text-center">
              <div className="text-white font-bold text-[16px] mb-1">Get Free Quote in 2 Mins</div>
              <div className="text-white/70 text-[12px] mb-4">Best Price Guaranteed · No spam calls</div>
              <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'d like a free quote for a ' + destName + ' trip.\n\nPlease share available packages and pricing.\n\nThanks!')}`} target="_blank" rel="noreferrer">
                <Button className="w-full bg-white text-brand-orange hover:bg-white/90 h-12 rounded-xl font-bold text-[15px] flex items-center justify-center gap-2 shadow-lg">
                  <MessageCircle size={18} /> Get Free Quote Now
                </Button>
              </a>
              <div className="text-white/50 text-[10px] mt-2">Reply in 5 min · Instant WhatsApp</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}